// W-n in comments are the review-warning ids of the committed plan,
// docs/superpowers/plans/2026-09-19-cubbo-2818-event-close-reconciliation-wms.md.
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonLoader } from '../../../components'
import { Loader } from '../../../components/Loader'
import { previewCoverageEvent } from '../../../services/coverageEventServices'
import { mapCoverageEventError } from './coverageEventErrors'

const T = 'cycle_counts.events.create'

export const CoverageEventPreviewStep = ({ warehouseId, filters, signature, onSignatureChange, onBack, onConfirm, busy, onGateOff, submitError }) => {
  const { i18n } = useTranslation()
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    previewCoverageEvent({ warehouse_id: warehouseId, filters })
      .then(data => {
        if (!alive) return
        setPreview(data)
        // A store-wide event always requires the merchant signature, overriding whatever a
        // previous (non-store-wide) preview set — the dialog never holds the preview, so it
        // cannot decide this itself (W-10).
        if (data.store_wide === true) onSignatureChange(true)
        else if (signature == null) onSignatureChange(data.merchant_signature_required_default === true)
      })
      .catch(err => {
        if (!alive) return
        const mapped = mapCoverageEventError(err, i18n)
        // onGateOff() can synchronously close the dialog that hosts this step (the tab hides
        // Create/this step on gate-off), unmounting it before the next line runs — re-check
        // `alive` (flipped by this effect's own cleanup) instead of setting state after that.
        if (mapped.gateOff) onGateOff()
        if (alive) setError(mapped.message)
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, JSON.stringify(filters)])

  if (loading) return <Loader show />
  if (error) return <p className="text-sm text-red-700">{error}</p>

  const nothing = (preview.to_spawn ?? 0) + (preview.watched ?? 0) === 0
  // A store-wide event always requires the merchant signature (W-10); the preview is non-null here.
  const signatureLocked = preview.store_wide === true
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-900">{i18n.t(`${T}.preview_title`)}</h3>
      <p className="text-sm text-gray-900">{i18n.t(`${T}.to_spawn`, { count: preview.to_spawn ?? 0 })}</p>
      <p className="text-sm text-gray-500">{i18n.t(`${T}.watched`, { count: preview.watched ?? 0 })}</p>
      {preview.store_wide && <p className="text-sm text-indigo-700">{i18n.t(`${T}.store_wide_hint`)}</p>}
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={signature === true || signatureLocked} disabled={signatureLocked} onChange={e => onSignatureChange(e.target.checked)} />
        {i18n.t(`${T}.signature_toggle`)}
        {signatureLocked && <span className="ml-2 text-xs text-gray-500">{i18n.t(`${T}.store_wide_signature_locked`)}</span>}
      </label>
      {submitError && <p className="text-sm text-red-700">{submitError}</p>}
      <div className="flex justify-between pt-2">
        <ButtonLoader type="secondary" onClick={onBack} disabled={busy}>
          {i18n.t('cycle_counts.bulk_assign.preview_step.back')}
        </ButtonLoader>
        <ButtonLoader type="primary" onClick={onConfirm} loading={busy} disabled={busy || nothing}>
          {i18n.t(`${T}.confirm`)}
        </ButtonLoader>
      </div>
    </div>
  )
}
